import { useEffect } from 'react';
// Custom property this hook writes on the measured element. Consumers read it
// through `var()`, so the measurement never travels through React state.
export const STICKY_FILL_HEIGHT_VAR = '--ama-sticky-fill-height';
/**
 * Sizes a `position: sticky` element to the space between its *current* top
 * edge and the bottom of the viewport, by writing the result to
 * `--ama-sticky-fill-height` on the element itself.
 *
 * Why measure at all: sticky only clamps an element once its in-flow top has
 * scrolled past `top: stickyTopOffset`. Until then (page load, scrolled to the
 * top) the element sits at its natural position, which is *below* that offset,
 * so a static `height: calc(100dvh - stickyTopOffset)` overflows the viewport
 * by the difference and whatever renders at the bottom of the box — for AMA,
 * the input and the storage notice — is cut off by `overflow: hidden`.
 *
 * The available height is `viewport height - max(currentTop, stickyTopOffset)`:
 *   - unstuck, near the top of the page → `currentTop` wins, giving a shorter box
 *   - stuck                             → the two are equal
 *   - past the end of the sticky range  → `stickyTopOffset` wins, capping the
 *     height so the element scrolls away with the page instead of stretching to
 *     keep its bottom edge glued to the viewport
 *
 * Why a custom property rather than returned state: the value changes on every
 * scroll frame while the element is unstuck. Through `useState` that re-renders
 * the whole page tree once per frame and forces callers to smuggle the value
 * past memoised subtrees. A custom property inherits, so descendants that need
 * the same height can read it directly, and the `var()` fallback covers the
 * pre-measurement paint — so there is no undefined-until-measured state and
 * nothing that could differ between the server and client render.
 */
export const useStickyFillHeight = (ref, { enabled, stickyTopOffset }) => {
    useEffect(() => {
        const el = ref.current;
        if (!el)
            return;
        if (!enabled) {
            el.style.removeProperty(STICKY_FILL_HEIGHT_VAR);
            return;
        }
        let raf = 0;
        let lastWritten = '';
        const measure = () => {
            const { top } = el.getBoundingClientRect();
            const available = Math.max(0, window.innerHeight - Math.max(top, stickyTopOffset));
            const next = `${Math.round(available)}px`;
            // Re-writing the same value would be a no-op for the browser anyway, but
            // the guard also stops the ResizeObserver below from ping-ponging: our
            // write changes this element's height, which changes the flex row's
            // height, which is a body resize.
            if (next === lastWritten)
                return;
            lastWritten = next;
            el.style.setProperty(STICKY_FILL_HEIGHT_VAR, next);
        };
        const scheduleMeasure = () => {
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(measure);
        };
        measure();
        window.addEventListener('scroll', scheduleMeasure, { passive: true });
        window.addEventListener('resize', scheduleMeasure);
        // The element's top also moves with no scroll and no viewport resize, in
        // two distinct ways that need two distinct observers:
        //
        //  1) Something above it changes size without any DOM insertion — a
        //     late-loading image getting its intrinsic dimensions, a font swap.
        //     That resizes the document.
        const resizeObserver = new ResizeObserver(scheduleMeasure);
        resizeObserver.observe(document.body);
        //  2) Something is *injected* above it after mount — the header
        //     microfrontend's smart-action banner is the known case. That can shift
        //     the wrapper's top without changing the body's own height, so watch
        //     for insertions rather than chasing selectors for whatever the header
        //     team adds next. (Changes to `stickyTopOffset` itself arrive as an
        //     effect dependency, so this is only about in-flow content.)
        const mutationObserver = new MutationObserver(scheduleMeasure);
        mutationObserver.observe(document.body, { childList: true, subtree: true });
        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener('scroll', scheduleMeasure);
            window.removeEventListener('resize', scheduleMeasure);
            resizeObserver.disconnect();
            mutationObserver.disconnect();
        };
    }, [enabled, ref, stickyTopOffset]);
};
